import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Code, Trophy, Calendar, Clock, ChevronRight, Star, 
  Zap, Target, Users, TrendingUp, Play, BookOpen, Award
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CodingPlatformPage = () => {
  const [potd, setPotd] = useState(null);
  const [contests, setContests] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({ total_problems: 0, total_users: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [potdRes, contestsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/coding/potd`),
        axios.get(`${API}/coding/contests?status=upcoming`),
        axios.get(`${API}/coding/leaderboard?limit=5`)
      ]);
      setPotd(potdRes.data);
      setContests(contestsRes.data.contests || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/30';
      case 'medium': return 'text-amber-500 bg-amber-500/10 border-amber-500/30';
      case 'hard': return 'text-rose-500 bg-rose-500/10 border-rose-500/30';
      default: return 'text-gray-500 bg-gray-500/10 border-gray-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Hero Section - Clean, Modern */}
      <section className="relative overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-200 rounded-full px-4 py-2 mb-8">
              <Zap className="text-indigo-600" size={16} />
              <span className="text-indigo-700 font-medium text-sm">Developer Engagement Platform</span>
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              <span className="text-slate-900">Code.</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600"> Compete.</span>
              <span className="text-slate-900"> Conquer.</span>
            </h1>

            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Sharpen your coding skills with daily challenges, compete in timed contests, 
              and climb the leaderboard. Join thousands of developers on their journey to excellence.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/coding/problems"
                className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40"
                data-testid="start-coding-btn"
              >
                <Play size={20} />
                Start Coding
              </Link>
              <Link
                to="/coding/contests"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-900 px-8 py-4 rounded-xl font-semibold transition-all border-2 border-slate-200 hover:border-slate-300"
                data-testid="view-contests-btn"
              >
                <Trophy size={20} />
                View Contests
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-200">
            {[
              { icon: Code, label: 'Problems', value: '100+' },
              { icon: Users, label: 'Active Users', value: '500+' },
              { icon: Trophy, label: 'Contests', value: '10+' },
              { icon: Target, label: 'Submissions', value: '5000+' }
            ].map((stat, idx) => (
              <div key={idx} className="py-8 px-6 text-center">
                <stat.icon className="mx-auto mb-3 text-indigo-600" size={28} />
                <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Problem of the Day - Large Card */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Star className="text-white" size={20} />
                    </div>
                    <div>
                      <h2 className="text-white font-bold text-lg">Problem of the Day</h2>
                      <p className="text-white/70 text-sm">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>
                  <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full font-medium">
                    +{potd?.problem?.difficulty === 'hard' ? '50' : potd?.problem?.difficulty === 'medium' ? '25' : '10'} pts
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                {loading ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                  </div>
                ) : potd?.problem ? (
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">{potd.problem.title}</h3>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(potd.problem.difficulty)}`}>
                            {potd.problem.difficulty?.toUpperCase()}
                          </span>
                          {potd.problem.tags?.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-slate-600 mb-6 line-clamp-2">
                      {potd.problem.description?.substring(0, 200)}...
                    </p>
                    <Link
                      to={`/coding/problem/${potd.problem.id}`}
                      className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-all"
                      data-testid="solve-potd-btn"
                    >
                      Solve Challenge
                      <ChevronRight size={18} />
                    </Link>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="mx-auto text-slate-300 mb-4" size={48} />
                    <p className="text-slate-500">No problem available today. Check back soon!</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Leaderboard Widget */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trophy className="text-amber-500" size={20} />
                    <h2 className="font-bold text-slate-900">Top Coders</h2>
                  </div>
                  <Link to="/coding/leaderboard" className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                    View All
                  </Link>
                </div>
              </div>
              
              <div className="divide-y divide-slate-100">
                {leaderboard.length > 0 ? leaderboard.map((user, idx) => (
                  <div key={idx} className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      idx === 0 ? 'bg-amber-100 text-amber-700' :
                      idx === 1 ? 'bg-slate-200 text-slate-700' :
                      idx === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{user.name || user.username}</div>
                      <div className="text-xs text-slate-500">{user.problems_solved} problems</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-indigo-600">{user.total_points}</div>
                      <div className="text-xs text-slate-500">points</div>
                    </div>
                  </div>
                )) : (
                  <div className="px-6 py-8 text-center text-slate-500">
                    No rankings yet. Be the first!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Contests */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Upcoming Contests</h2>
            <p className="text-slate-600 mt-1">Compete with developers from around the world</p>
          </div>
          <Link 
            to="/coding/contests" 
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
          >
            All Contests
            <ChevronRight size={18} />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contests.length > 0 ? contests.slice(0, 3).map((contest, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Trophy className="text-white" size={24} />
                </div>
                <span className="bg-indigo-100 text-indigo-700 text-xs px-3 py-1 rounded-full font-medium">
                  {contest.is_rated ? 'Rated' : 'Practice'}
                </span>
              </div>
              
              <h3 className="font-bold text-slate-900 text-lg mb-2">{contest.title}</h3>
              <p className="text-slate-600 text-sm mb-4 line-clamp-2">{contest.description}</p>
              
              <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                <div className="flex items-center gap-1">
                  <Calendar size={14} />
                  <span>{new Date(contest.start_time).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  <span>{new Date(contest.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
              
              <Link
                to={`/coding/contest/${contest.id}`}
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                View Details
                <ChevronRight size={16} />
              </Link>
            </div>
          )) : (
            <div className="col-span-full bg-slate-50 rounded-xl p-12 text-center">
              <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
              <h3 className="font-semibold text-slate-700 mb-2">No Upcoming Contests</h3>
              <p className="text-slate-500">Check back soon for new contests!</p>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Code With Us?</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Everything you need to level up your coding skills and prepare for technical interviews
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Code, title: 'Curated Problems', desc: 'Hand-picked problems covering all difficulty levels and topics' },
              { icon: Trophy, title: 'Live Contests', desc: 'Compete in real-time with developers worldwide' },
              { icon: TrendingUp, title: 'Track Progress', desc: 'Detailed analytics and progress tracking' },
              { icon: Award, title: 'Earn Badges', desc: 'Unlock achievements and showcase your skills' }
            ].map((feature, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-indigo-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="text-indigo-400" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Coding Journey?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Join our community and start solving problems today. No credit card required.
          </p>
          <Link
            to="/coding/problems"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-indigo-600 px-8 py-4 rounded-xl font-semibold transition-all shadow-lg"
            data-testid="get-started-btn"
          >
            <Zap size={20} />
            Get Started Free
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CodingPlatformPage;
